<template>
    <div class="af-search-filter-salary afu-my-4 afu-pt-5 afu-pb-5 afu-px-4 afu-shadow-secondary afu-rounded-secondary afu-border-neutral-light afu-bg-neutral-light">
        <ValidationObserver
            ref="formSalaryFilter"
            tag="form"
            class="af-salary-form"
            @submit.prevent="apply()"
        >
            <div class="afu-mb-3">
                <C1Label
                    label-for="Salary Type"
                    class="afu-mb-3 afu-text-secondary afu-flex afu-justify-between"
                >
                    <span class="af-text--5xl afu-uppercase afu-font-bold afu-tracking-widest">
                        {{ $t('label.salary') }}
                    </span>
                    <a
                        v-if="isSalaryFilter"
                        class="afu-no-underline afu-text-sm afu-capitalize afu-font-normal afu-text-black"
                        @click.stop.prevent="reset()"
                        href="#"
                    >
                        {{ $t('label.reset') }}
                    </a>
                </C1Label>
                <ValidationProvider
                    v-slot="{ errors }"
                    name="Salary Type"
                    rules="required|object:id"
                >
                    <C1Select
                        v-model="selectedType"
                        label=""
                        label-for="filterBySalary"
                        :options="salary.types"
                        :placeholder="$t('label.select_value', {'value': $t('label.salary_type')} )"
                        :is-invalid="!!errors[0]"
                        :invalid-feedback="errors[0]"
                    />
                </ValidationProvider>
            </div>

            <div
                v-if="salaryCurrencies"
                class="afu-mb-3"
            >
                <C1Select
                    v-model="salary.currency"
                    label=""
                    label-for="filterByCurrency"
                    :options="salaryCurrencies"
                    :placeholder="$t('label.salary_any_currency')"
                />
            </div>

            <div class="afu-mb-3">
                <div class="afu-flex afu-items-start">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="min"
                        rules="required|numeric|lte:@max"
                    >
                        <C1FormInput
                            id="minSalary"
                            v-model="salaryMin"
                            :placeholder="$t('label.min')"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                        >
                            <template v-slot:icon>
                                {{ selectedCurrency }}
                            </template>
                        </C1FormInput>
                    </ValidationProvider>
                    <FontAwesomeIcon
                        :icon="['fal', 'minus']"
                        size="xs"
                        class="afu-mx-2 afu-mt-4 afu-text-secondary"
                    />
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="max"
                        rules="required|numeric|gte:@min"
                    >
                        <C1FormInput
                            id="maxSalary"
                            v-model="salaryMax"
                            :placeholder="$t('label.max')"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                        >
                            <template v-slot:icon>
                                {{ selectedCurrency }}
                            </template>
                        </C1FormInput>
                    </ValidationProvider>
                </div>
            </div>

            <C1Button
                type="button"
                variant="outline-secondary"
                block
                class="af-filter-go-btn"
                @click.stop.prevent="handleSalaryFilter()"
            >
                {{ $t('label.go') }}
            </C1Button>
        </ValidationObserver>
    </div>
</template>

<script>
    import {mapMutations, mapState} from 'vuex';
    import isEmpty from 'lodash/isEmpty';
    import {ValidationObserver, ValidationProvider} from 'vee-validate';

    import {Bus} from '@/utils/bus';
    import C1Select from '@/components/C1Components/C1Select';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1Button from '@/components/C1Components/C1Button';
    import C1Label from '@/components/C1Components/C1Label';

    export default {
        name: 'SalaryFilter',

        components: {
            ValidationObserver,
            ValidationProvider,
            C1Select,
            C1FormInput,
            C1Button,
            C1Label,
        },

        data() {
            return {
                salary: {
                    types: [
                        {
                            id: 'annual_salary',
                            label: 'Annual salary',
                        },
                        {
                            id: 'annual_salary_plus_commission',
                            label: 'Annual salary + Commission',
                        },
                        {
                            id: 'hourly_rate',
                            label: 'Hourly rate',
                        },
                        {
                            id: 'per_day',
                            label: 'Per day',
                        },
                    ],
                    min: null,
                    max: null,
                    selectedType: {},
                    currency: this.getAnyCurrencyOption(),
                },
            };
        },

        computed: {
            ...mapState({
                $_searchFilters: state => state.search.searchPayload?.search_filters,
                $_searchAggregationAll: state => state.search.searchPayload?.search_results?.aggregation_all,
                $_searchMeta: state => state.search.searchPayload?.meta,
            }),

            isSalaryFilter() {
                return !isEmpty(this.selectedType);
            },

            selectedType: {
                // getter
                get: function() {
                    if (!isEmpty(this.salary.selectedType)) {
                        return this.salary.selectedType;
                    }
                    if (this.$_searchFilters?.display_pay_type) {
                        const result = this.salary.types.filter(o => {
                            return o.id === this.$_searchFilters?.display_pay_type;
                        });
                        return result[0];
                    }
                    return {
                        id: 'annual_salary',
                        label: 'Annual salary',
                    };
                },
                // setter
                set: function(newValue) {
                    this.salary.selectedType = newValue;
                },
            },
            salaryMax: {
                // getter
                get: function() {
                    if (this.salary.max || this.salary.max === '') {
                        return this.salary.max;
                    }
                    if (this.$_searchFilters?.pay_max?.value) {
                        let pay_max = parseInt(this.$_searchFilters?.pay_max.value);
                        return pay_max;
                    }
                    return this.salary.max;
                },
                // setter
                set: function(newValue) {
                    this.salary.max = newValue;
                },
            },
            salaryMin: {
                // getter
                get: function() {
                    if (this.salary.min || this.salary.min === '') {
                        return this.salary.min;
                    }
                    if (this.$_searchFilters?.pay_min?.value) {
                        let pay_min = parseInt(this.$_searchFilters?.pay_min.value);
                        return pay_min;
                    }
                    return this.salary.min;
                },
                // setter
                set: function(newValue) {
                    this.salary.min = newValue;
                },
            },

            salaryCurrencies() {
                if (!this.$_searchAggregationAll?.pay_currency || this.$_searchAggregationAll.pay_currency.length === 0) {
                    return null;
                }

                if (this.$_searchAggregationAll.pay_currency.length === 1) {
                    if (this.$_searchMeta.default_currency?.id === this.$_searchAggregationAll.pay_currency[0].id) {
                        return null;
                    }
                }

                const currencies = this.$_searchAggregationAll.pay_currency.map((payCurrency) => {
                    payCurrency = {...payCurrency};

                    payCurrency.label = payCurrency.name + ' (' + payCurrency.code + ')';

                    return payCurrency;
                });

                currencies.unshift(this.getAnyCurrencyOption());

                return currencies;
            },

            selectedCurrency() {
                if (this.salaryCurrencies) {
                    return this.salary.currency.symbol;
                }

                return this.$t('global.currency_symbol');
            },
        },

        created() {
            Bus.$on('resetSalaryFilter', () => {
                this.reset();
            });
        },

        watch: {
            '$_searchFilters'() {
                this.setSelectedCurrency();
            },
        },

        methods: {
            ...mapMutations('search', {
                $_SET_SEARCH_FILTERS: 'SET_SEARCH_FILTERS',
            }),

            async handleSalaryFilter() {
                const validate = await this.$refs.formSalaryFilter.validate();
                if (!validate) {
                    return;
                }

                if (this.selectedType.id) {
                    const pay_min = {
                        value: this.salaryMin,
                        pay_type_code: this.selectedType.id,
                    };

                    const pay_max = {
                        value: this.salaryMax,
                        pay_type_code: this.selectedType.id,
                    };

                    this.$_SET_SEARCH_FILTERS({
                        key: 'pay_min',
                        value: pay_min,
                        preventSearch: true,
                    });
                    this.$_SET_SEARCH_FILTERS({
                        key: 'pay_max',
                        value: pay_max,
                        preventSearch: true,
                    });
                    this.$_SET_SEARCH_FILTERS({
                        key: 'display_pay_type',
                        value: this.selectedType.id,
                        preventSearch: true,
                    });
                    this.$_SET_SEARCH_FILTERS({
                        key: 'pay_currency_id',
                        value: this.salary.currency.id,
                        preventSearch: true,
                    });
                    this.$emit('go');
                }
            },

            reset() {
                this.salaryMin = '';
                this.salaryMax = '';
                this.selectedType = {};
                this.salary.currency = this.getAnyCurrencyOption();
                this.$_SET_SEARCH_FILTERS({
                    key: 'pay_min',
                    value: {},
                    preventSearch: true,
                });
                this.$_SET_SEARCH_FILTERS({
                    key: 'pay_max',
                    value: {},
                    preventSearch: true,
                });
                this.$_SET_SEARCH_FILTERS({
                    key: 'display_pay_type',
                    value: null,
                    preventSearch: true,
                });
                this.$_SET_SEARCH_FILTERS({
                    key: 'pay_currency_id',
                    value: null,
                    preventSearch: true,
                });
                this.$emit('go');
                this.$refs.formSalaryFilter.reset();
            },

            getAnyCurrencyOption() {
                return {
                    id: null,
                    code: null,
                    name: null,
                    count: null,
                    symbol: null,
                    label: this.$t('label.salary_any_currency'),
                };
            },

            setSelectedCurrency() {
                if (!this.$_searchFilters?.pay_currency_id || !this.salaryCurrencies) {
                    return;
                }

                this.salaryCurrencies.forEach((currency) => {
                    if (currency.id === this.$_searchFilters.pay_currency_id) {
                        this.salary.currency = currency;
                    }
                });
            },
        },
    };
</script>
